'use client'

import { useState } from 'react'
import { CheckCircle, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { Text } from '@/components/ui/text'
import { useModal } from './ModalContext'

export default function ModalForm() {
  const { activeModal, closeModal } = useModal()
  const isOpen = activeModal === 'form'
  const [submitted, setSubmitted] = useState(false)
  const t = useTranslations('modals.form')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: wire up to API route
    setSubmitted(true)
  }

  if (!isOpen) return null

  const inputClass =
    'w-full rounded-xl border border-border bg-white px-4 py-3 text-[15px] text-text-primary placeholder:text-text-subtle focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20'
  const labelClass = 'mb-2 block text-sm font-medium text-text-muted'

  const callTimeOptions = t.raw('fields.callTimeOptions') as string[]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-text-primary/40 backdrop-blur-sm"
        onClick={closeModal}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-3xl border border-border bg-white p-8 shadow-[0_40px_80px_-20px_rgba(43,90,255,0.25)]">
        <div className="mb-6 flex items-center justify-between">
          <Heading as="h3" size="h3">{t('title')}</Heading>
          <button
            type="button"
            onClick={closeModal}
            aria-label={t('closeLabel')}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-text-muted transition-colors hover:border-blue hover:text-blue"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-grad-cta text-white shadow-[0_10px_30px_-10px_rgba(43,90,255,0.5)]">
              <CheckCircle size={32} strokeWidth={1.75} />
            </div>
            <Heading as="h4" size="h4">{t('successTitle')}</Heading>
            <Text tone="muted">{t('successDesc')}</Text>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>{t('fields.fullName')}</label>
                <input type="text" className={inputClass} placeholder={t('fields.fullNamePlaceholder')} required />
              </div>
              <div>
                <label className={labelClass}>{t('fields.businessName')}</label>
                <input type="text" className={inputClass} placeholder={t('fields.businessNamePlaceholder')} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass}>{t('fields.email')}</label>
                <input type="email" className={inputClass} placeholder={t('fields.emailPlaceholder')} required />
              </div>
              <div>
                <label className={labelClass}>{t('fields.phone')}</label>
                <input type="tel" className={inputClass} placeholder={t('fields.phonePlaceholder')} />
              </div>
            </div>
            <div>
              <label className={labelClass}>{t('fields.message')}</label>
              <textarea
                className={`${inputClass} min-h-[120px] resize-y`}
                placeholder={t('fields.messagePlaceholder')}
              />
            </div>
            <div>
              <label className={labelClass}>{t('fields.callTime')}</label>
              <select className={inputClass} defaultValue="">
                <option value="" disabled>{t('fields.callTimePlaceholder')}</option>
                {callTimeOptions.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <Button type="submit" size="lg" className="w-full">
              {t('submit')}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
